import { useSyncExternalStore } from "react";

/**
 * Installing the portal as an app. Chromium browsers (Android, desktop Chrome and Edge) offer installation through a
 * `beforeinstallprompt` event, which can arrive before React has drawn anything, so it is caught here as soon as the module
 * loads and kept until the person asks to install. Safari on an iPhone or iPad has no such event: installing there is Share,
 * then Add to Home Screen, which the portal can only explain.
 */

/** The event Chromium fires when the portal can be installed. Not in the DOM typings. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

/** How the portal can be installed here, if at all. */
export type InstallOption = "prompt" | "ios" | "none";

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) {
    listener();
  }
}

/** Whether the portal is already running as an installed app. */
function isStandalone(): boolean {
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return iosStandalone || (typeof window.matchMedia === "function" && window.matchMedia("(display-mode: standalone)").matches);
}

/** Whether this is Safari's engine on an iPhone or iPad, where installing is Add to Home Screen. iPadOS reports itself as a Mac with touch. */
function isAppleMobile(): boolean {
  const iPadAsMac = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return /iPhone|iPad|iPod/.test(navigator.userAgent) || iPadAsMac;
}

function currentOption(): InstallOption {
  if (isStandalone()) {
    return "none";
  }
  if (deferredPrompt) {
    return "prompt";
  }
  return isAppleMobile() ? "ios" : "none";
}

/**
 * Starts listening for the browser's install offer and registers the service worker. Called once, as the app starts. The service
 * worker is registered only in a production build: under the Vite dev server it would sit between the page and hot reloading.
 */
export function initialiseInstallSupport(): void {
  window.addEventListener("beforeinstallprompt", (event) => {
    // Keep the offer for the person's own "Install app" rather than letting the browser show its own banner at a random moment.
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    notify();
  });

  if (import.meta.env.PROD && "serviceWorker" in navigator) {
    window.addEventListener("load", () => {
      void navigator.serviceWorker.register("/sw.js").catch(() => {
        // Installing is a convenience; a portal that cannot register its worker still works as a website.
      });
    });
  }
}

/** Shows the browser's own install dialog, when it has offered one. The offer can be used once. */
export async function promptInstall(): Promise<void> {
  const prompt = deferredPrompt;
  if (!prompt) {
    return;
  }
  deferredPrompt = null;
  notify();
  await prompt.prompt();
  await prompt.userChoice;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** How the portal can be installed in this browser right now; re-renders when the browser makes or withdraws its offer. */
export function useInstallOption(): InstallOption {
  return useSyncExternalStore(subscribe, currentOption, () => "none");
}
